import { Link } from "react-router-dom";
import "./FeaturedProject.css";

/* ============================================================
   CONFIGURATION

   Choose which example is displayed using the export below.

   In project mode:
   - undefined = inherit the project's value
   - "" = intentionally hide optional text
   - visual.image: null = use the text mark instead of an image

   Product links open the project's page by default.
   Set link: null to hide the button entirely.
   ============================================================ */

const examples = {
  project: {
    enabled: true, // false hides the entire section
    mode: "project",
    projectPath: "/pvp", // Must match a path in your products array

    eyebrow: "Currently working on",
    title: undefined, // Defaults to product.name
    description: undefined, // Defaults to product.description

    // Optional extra line about your current work. "" hides it.
    noteLabel: "On the workbench",
    note: "Currently improving combat balance and polishing the multiplayer experience.",

    showStatus: false, // Show the project's status badge

    link: {
      label: "Explore the project",
      href: undefined, // Defaults to the internal project page, e.g. /pvp
      newTab: false,
    },

    appearance: {
      accent: undefined, // Defaults to product.accent
      background: "var(--surface, #242427)",
      borderRadius: "24px",
      padding: "32px",
      visualWidth: "38%", // Width of the artwork on desktop
      visualPosition: "right", // "left" or "right"
    },

    visual: {
      show: true,
      image: undefined, // Inherit product.image; null forces a text mark
      mark: undefined, // Defaults to product.mark
      fit: "contain", // "contain" for logos; "cover" for screenshots
      padding: "24px", // Use "0px" for edge-to-edge images
      aspectRatio: "16 / 10",
      background: "#141416",
      glowSize: "180px",
      animate: true,
      showGrid: true,
    },
  },

  custom: {
    enabled: true,
    mode: "custom", // Does not need to match any project

    eyebrow: "What I'm doing right now",
    title: "Trying something new",
    description:
      "Experimenting with local AI tools and integration projects.",

    noteLabel: "Current focus",
    note: "Building a server so I can use local AI and integrate it into useful tools and workflows.",

    showStatus: false,
    status: "Experimenting", // Custom mode can have its own status

    // Use null when there is nowhere to link.
    // Alternatively: { label: "More about me", href: "/about", newTab: false }
    link: null,

    appearance: {
      accent: "#cacaca",
      background: "var(--surface, #242427)",
      borderRadius: "24px",
      padding: "32px",
      visualWidth: "38%",
      visualPosition: "right",
    },

    visual: {
      show: true,
      image: "/images/5090.png", // Or an image URL such as "/images/experiment.png"
      mark: "SERVER",
      fit: "cover",
      padding: "24px",
      aspectRatio: "16 / 10",
      background: "#141416",
      glowSize: "120px",
      animate: true,
      showGrid: true,
    },
  },
};

// CHANGE THIS to examples.custom to show the standalone update.
export const featuredProjectConfig = examples.custom;

/* ============================================================
   COMPONENT
   Everything below can remain unchanged.
   ============================================================ */

function seedFrom(text, salt) {
  let hash = 2166136261 ^ salt;

  for (const char of text) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  }

  return ((hash >>> 0) % 1000) / 1000;
}

function FeatureLink({ href, newTab, children }) {
  const props = {
    className: "fp-button",
    ...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {}),
  };

  // Use React Router for same-site page navigation.
  if (href.startsWith("/") && !href.startsWith("//")) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}

export default function FeaturedProject({
  config = featuredProjectConfig,
  products = [],
}) {
  if (!config || config.enabled === false) return null;

  const isProject = config.mode === "project";
  const product = isProject
    ? products.find((item) => item.path === config.projectPath)
    : null;

  // Avoid showing an incorrect feature if the configured path doesn't exist.
  if (isProject && !product) return null;

  const appearance = config.appearance ?? {};
  const visual = config.visual ?? {};

  const title = config.title ?? product?.name ?? "Currently working on";
  const description = config.description ?? product?.description ?? "";
  const status = config.status ?? product?.status;

  // Unlike ??, this allows null to intentionally disable the inherited image.
  const image =
    visual.image === undefined ? product?.image : visual.image;

  const mark = visual.mark ?? product?.mark ?? "NOW";
  const showVisual = visual.show !== false;

  const href =
    config.link === null
      ? null
      : config.link?.href ?? product?.path;

  const style = {
    "--fp-accent": appearance.accent ?? product?.accent ?? "#c9d4e0",
    "--fp-background": appearance.background ?? "var(--surface, #242427)",
    "--fp-radius": appearance.borderRadius ?? "24px",
    "--fp-padding": appearance.padding ?? "32px",
    "--fp-visual-width": appearance.visualWidth ?? "38%",
    "--fp-art-background": visual.background ?? "#141416",
    "--fp-art-ratio": visual.aspectRatio ?? "16 / 10",
    "--fp-image-fit": visual.fit ?? "contain",
    "--fp-image-padding": visual.padding ?? "24px",
    "--fp-glow-size": visual.glowSize ?? "180px",
    "--fp-s1": seedFrom(title, 1),
    "--fp-s2": seedFrom(title, 2),
  };

  return (
    <section
      className="featured-project"
      style={style}
      data-visual={showVisual ? "visible" : "hidden"}
      data-position={appearance.visualPosition ?? "right"}
      aria-label={config.eyebrow || "Featured update"}
    >
      <div className="fp-copy">
        {(config.eyebrow || (config.showStatus !== false && status)) && (
          <div className="fp-meta">
            {config.eyebrow && (
              <p className="fp-eyebrow">{config.eyebrow}</p>
            )}

            {config.showStatus !== false && status && (
              <span
                className="fp-status"
                data-status={status.toLowerCase().replace(/\s+/g, "-")}
              >
                {status}
              </span>
            )}
          </div>
        )}

        <h2 className="fp-title">{title}</h2>

        {description && (
          <p className="fp-description">{description}</p>
        )}

        {config.note && (
          <div className="fp-note">
            {config.noteLabel && (
              <p className="fp-note-label">{config.noteLabel}</p>
            )}
            <p className="fp-note-text">{config.note}</p>
          </div>
        )}

        {href && (
          <FeatureLink href={href} newTab={config.link?.newTab}>
            {config.link?.label ?? "Explore the project"}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 12h16m-6-6 6 6-6 6" />
            </svg>
          </FeatureLink>
        )}
      </div>

      {showVisual && (
        <div
          className="fp-visual"
          data-animated={visual.animate !== false}
          data-grid={visual.showGrid !== false}
          aria-hidden="true"
        >
          {image ? (
            <img
              className="fp-image"
              src={image}
              alt=""
              loading="lazy"
            />
          ) : (
            <span className="fp-mark">{mark}</span>
          )}
        </div>
      )}
    </section>
  );
}